import { datadogRum } from '@datadog/browser-rum';
import { useLocation } from '@tanstack/react-router';
import { createContext, useCallback, useEffect, useState } from 'react';

import { isExpectedApiError, registerGlobalApiErrorHandler } from '../helpers/api-errors';

type AlertKind = 'Success' | 'Error' | 'Info' | 'Warning';

type AlertOptions = {
  autoCloseAfterTimeMS?: number;
  keepAfterRouteChange?: boolean;
  details?: string;
};

// Options for an alert that reports a caught error. `cause` is the error itself,
// and it never reaches the rendered toast — it only decides whether the toast is
// raised at all (see addErrorAlert). Pass it wherever one is in hand.
type ErrorAlertOptions = AlertOptions & {
  cause?: unknown;
};

export type Alert = {
  id: string;
  message: string;
  kind: AlertKind;
} & AlertOptions;

type AlertContext = {
  alerts: Alert[];
  addErrorAlert: (message: string, options?: ErrorAlertOptions) => void;
  addSuccessAlert: (message: string, options?: AlertOptions) => void;
  addInfoAlert: (message: string, options?: AlertOptions) => void;
  addWarningAlert: (message: string, options?: AlertOptions) => void;
  removeAlert: (alert: Alert) => void;
};

export const AlertContext = createContext<AlertContext>({
  alerts: [],
  addErrorAlert: () => {},
  addSuccessAlert: () => {},
  addInfoAlert: () => {},
  addWarningAlert: () => {},
  removeAlert: () => {},
});

const alertDefaults: AlertOptions = {
  keepAfterRouteChange: false,
  autoCloseAfterTimeMS: 30_000,
  details: undefined,
};

export const AlertContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const addAlert = useCallback((message: string, kind: AlertKind, options?: AlertOptions) => {
    const alert: Alert = {
      ...alertDefaults,
      ...options,
      message,
      kind,
      id: crypto.randomUUID(),
    };
    setAlerts((alerts) => {
      // Skip a toast identical to one already on screen. The global cache handler
      // can fire several times for the same denial (e.g. a page whose products,
      // vendors, and orders queries all 403 at once); collapse those into one.
      if (alerts.some((existing) => existing.message === message && existing.kind === kind)) {
        return alerts;
      }

      return [...alerts, alert];
    });

    // auto close alert if required (harmless no-op if it was a skipped duplicate)
    if (alert.autoCloseAfterTimeMS) {
      setTimeout(() => {
        setAlerts((alerts) => alerts.filter((x) => x.id !== alert.id));
      }, alert.autoCloseAfterTimeMS);
    }
  }, []);

  const addErrorAlert = useCallback(
    (message: string, { cause, ...options }: ErrorAlertOptions = {}) => {
      // Some API failures are an account state, not a defect: 401 and 403 are
      // presented once by the global cache handler as a warning, and 402 becomes
      // the store-disabled takeover. Drop the per-hook duplicate here so the user
      // doesn't see it twice — and, crucially, so it isn't logged to Datadog as an
      // error, which created alerting noise.
      //
      // This keys on the error, not on the message text. Matching the rendered
      // prose only worked for the callers that interpolated `err.message`, and
      // silently did nothing for the ones with a hardcoded message.
      if (isExpectedApiError(cause)) {
        return;
      }

      addAlert(message, 'Error', options);
      datadogRum.addError(message);
    },
    [addAlert]
  );

  const addSuccessAlert = useCallback(
    (message: string, options?: AlertOptions) => {
      addAlert(message, 'Success', options);
    },
    [addAlert]
  );

  const addInfoAlert = useCallback(
    (message: string, options?: AlertOptions) => {
      addAlert(message, 'Info', options);
    },
    [addAlert]
  );

  const addWarningAlert = useCallback(
    (message: string, options?: AlertOptions) => {
      addAlert(message, 'Warning', options);
    },
    [addAlert]
  );

  const removeAlert = useCallback((alert: Alert) => {
    setAlerts((alerts) => alerts.filter((x) => x.id !== alert.id));
  }, []);

  // Present permission (403) and session-expiry (401) errors thrown anywhere in
  // the app as a single warning toast. The QueryClient's cache handlers route
  // these here; using a warning (not addErrorAlert) keeps them out of Datadog.
  useEffect(() => {
    registerGlobalApiErrorHandler((error) => addWarningAlert(error.message));

    return () => registerGlobalApiErrorHandler(null);
  }, [addWarningAlert]);

  // clear alerts on location change
  useEffect(() => {
    // TODO: check if this is actually needed or works
    // don't clear if pathname has trailing slash because this will be auto redirected again
    if (pathname.endsWith('/')) {
      return;
    }

    setAlerts((alerts) => {
      const filteredAlerts = alerts.filter((x) => x.keepAfterRouteChange);
      // if there were no alerts to remove, return the original array to not cause unnecessary renders
      if (filteredAlerts.length === alerts.length) {
        return alerts;
      }

      return filteredAlerts;
    });
  }, [pathname]);

  return (
    <AlertContext.Provider
      value={{
        alerts,
        addErrorAlert,
        addSuccessAlert,
        addInfoAlert,
        addWarningAlert,
        removeAlert,
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};
